import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetUtilityAdminBulkPurchasesQuery } from "../../../../service/bulk-purchase-service";
import { useGetUtilitiesForAdminQuery } from "../../../../service/utility-service";
import { BulkPurchaseTransaction } from "../../../../model/bulk-purchase/bulk-purchase";
import CommonPagination from "../../../../common/components/pagination/CommonPagination";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import UtilityPageLayout from "../../component/layout/UtilityPageLayout";
import { CardIcon, PaymentIcon, SearchIcon, ThreeDotsIcon, WalletIcon } from "../../../../assets/icons";
import styles from "./BulkPurchasePage.module.scss";
import { getCurrentUtilityId } from "../../../../guards/common/authentification-functions";
import { formatDateTime, getStatusLabel, getStatusVariant } from "../../../admin/bulk-purchase/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApplicationRoutes } from "../../../../routes";
import { FeatureFlagName } from "../../../../tenant/features/featureFlags";

type PaymentMethodConfig = {
	label: string;
	Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const PAYMENT_METHODS: Record<number, PaymentMethodConfig> = {
	1: { label: "Payment Hub", Icon: CardIcon },
	2: { label: "Cash Payment", Icon: WalletIcon },
};

const getPaymentMethodConfig = (type: number): PaymentMethodConfig => {
	return PAYMENT_METHODS[type] ?? { label: "Unknown", Icon: PaymentIcon };
};

const BulkPurchasePage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const pageSize = 10;

	const initialSearchTerm = searchParams.get("search") ?? "";
	const initialPage = parseInt(searchParams.get("page") ?? "1", 10);

	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [currentPage, setCurrentPage] = useState(Number.isNaN(initialPage) ? 1 : initialPage);

	const {
		updateLayout,
	} = useConfigLayout({
		showCountrySelector: false,
		utility: null,
		showExportButton: false,
		showBreadCrumb: false,
	});

	useEffect(() => {
		updateLayout({
			showCountrySelector: false,
			showExportButton: false,
			showBreadCrumb: false,
			country: null,
			utility: null,
		});
	}, [updateLayout]);

	const utilityId = useMemo(() => getCurrentUtilityId(), []);

	const {
		data: utilityDetails,
		isLoading: isUtilityDetailsLoading,
		isFetching: isFetchingUtilityDetails,
	} = useGetUtilitiesForAdminQuery(undefined, { skip: !utilityId });

	useEffect(() => {
		if (utilityDetails) {
			updateLayout({ utility: utilityDetails });
		}
	}, [utilityDetails, updateLayout]);

	const {
		data,
		isLoading: isBulkPurchasesLoading,
		isFetching: isBulkPurchasesFetching,
		error: bulkPurchasesError,
	} = useGetUtilityAdminBulkPurchasesQuery(
		{
			page: currentPage,
			pageSize,
			search: searchTerm || undefined,
			utilityId: utilityId ?? undefined,
		},
		{ skip: !utilityId }
	);

	const isLoading =
		isBulkPurchasesLoading ||
		isBulkPurchasesFetching ||
		isUtilityDetailsLoading ||
		isFetchingUtilityDetails;

	const [totalPages, setTotalPages] = useState(1);

	useEffect(() => {
		if (data) {
			setTotalPages(data.totalPages);
		}
	}, [data]);

	useEffect(() => {
		const paramSearch = searchParams.get("search") ?? "";
		if (paramSearch !== searchTerm) {
			setSearchTerm(paramSearch);
		}
		const paramPage = parseInt(searchParams.get("page") ?? "1", 10);
		const normalizedPage = Number.isNaN(paramPage) ? 1 : paramPage;
		if (normalizedPage !== currentPage) {
			setCurrentPage(normalizedPage);
		}
	}, [searchParams]);

	const getTotalMeters = (transaction: BulkPurchaseTransaction) => {
		return (
			(transaction.bulkPurchaseDetails?.vendingDetailsList?.length || 0) +
			(transaction.bulkPurchaseDetails?.billingDetailsList?.length || 0)
		);
	};

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1);

		const params = new URLSearchParams(searchParams);
		if (value) {
			params.set("search", value);
		} else {
			params.delete("search");
		}
		params.delete("page");
		setSearchParams(params, { replace: true });
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		const params = new URLSearchParams(searchParams);
		if (searchTerm) {
			params.set("search", searchTerm);
		} else {
			params.delete("search");
		}
		if (page > 1) {
			params.set("page", page.toString());
		} else {
			params.delete("page");
		}
		setSearchParams(params, { replace: true });
	};

	const handleOpenDetails = useCallback(
		(reference: string) => {
			if (!reference) {
				return;
			}
			const params = new URLSearchParams();
			if (searchTerm) {
				params.set("search", searchTerm);
			}
			if (currentPage > 1) {
				params.set("page", currentPage.toString());
			}
			const searchString = params.toString();

			navigate(
				`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}/${ApplicationRoutes.BULK_PURCHASE}/details/${encodeURIComponent(reference)}`,
				{
					state: searchString ? { bulkPurchaseSearch: searchString } : undefined,
				}
			);
		},
		[currentPage, navigate, searchTerm]
	);

	const renderTableRows = () => {
		if (isLoading) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>Loading bulk purchases...</td>
				</tr>
			);
		}

		if (bulkPurchasesError) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>Unable to load bulk purchases.</td>
				</tr>
			);
		}

		const items = data?.items ?? [];

		if (items.length === 0) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>No bulk purchases found.</td>
				</tr>
			);
		}

		return items.map((transaction: BulkPurchaseTransaction) => {
			const { Icon: MethodIcon, label } = getPaymentMethodConfig(transaction.type);

			return (
				<tr key={transaction.id}>
					<td>
						<div className={styles.purchaserDetails}>
							<div className={styles.purchaserName}>
								{transaction.user.firstName} {transaction.user.lastName}
							</div>
						</div>
					</td>
					<td className={styles.referenceCell}>{transaction.reference}</td>
					<td className={styles.dateCell}>{formatDateTime(transaction.createdAt)}</td>
					<td className={styles.metersCell}>
						<span className={styles.metersLabel}>Total Number of Meters:</span>
						<span className={styles.metersBadge}>{getTotalMeters(transaction)}</span>
					</td>
					<td className={styles.statusCell}>
						<span className={`${styles.statusBadge} ${styles[`status${getStatusVariant(transaction.status)}`]}`}>
							{getStatusLabel(transaction.status)}
						</span>
					</td>
					<td className={styles.paymentCell}>
						<div className={styles.paymentBadge}>
							<span className={styles.paymentIcon}>
								<MethodIcon />
							</span>
							<span>{label}</span>
						</div>
					</td>
					<td className={styles.actionCell}>
						<button
							type="button"
							className={styles.optionsButton}
							onClick={() => handleOpenDetails(transaction.reference)}
							aria-label="View bulk purchase details"
						>
							<ThreeDotsIcon />
						</button>
					</td>
				</tr>
			);
		});
	};

	if (!utilityId) {
		return (
			<UtilityPageLayout title="Bulk Purchases" feature={FeatureFlagName.ADMIN_BULK_PURCHASES}>
				<div className={styles.bulkPurchaseWrapper}>
					<div className={styles.loadingContainer}>
						<div className={styles.loadingSpinner}>Utility information unavailable.</div>
					</div>
				</div>
			</UtilityPageLayout>
		);
	}

	return (
		<UtilityPageLayout title="Bulk Purchases" feature={FeatureFlagName.ADMIN_BULK_PURCHASES}>
			<div className={styles.bulkPurchaseWrapper}>
				{/* {utilityDetails && (
					<div className={styles.utilityHeader}>
						<span className={styles.utilityLabel}>Utility</span>
						<h2 className={styles.utilityName}>{utilityDetails.name}</h2>
					</div>
				)} */}

				<div className={styles.searchContainer}>
					<div className={styles.searchInput}>
						<input
							type="text"
							placeholder="Search by Name"
							value={searchTerm}
							onChange={(event) => handleSearchChange(event.target.value)}
						/>
						<span className={styles.searchIcon}>
							<SearchIcon />
						</span>
					</div>
				</div>

				<div className={styles.tableContainer}>
					<table className={styles.transactionsTable}>
						<thead>
							<tr>
								<th scope="col">Purchaser</th>
								<th scope="col">Reference</th>
								<th scope="col">Date &amp; Time</th>
								<th scope="col">Total Meters</th>
								<th scope="col">Status</th>
								<th scope="col">Payment Method</th>
								<th scope="col" className={styles.actionHeader}>
									Action
								</th>
							</tr>
						</thead>
						<tbody>{renderTableRows()}</tbody>
					</table>
				</div>

				{data && data.totalPages > 1 && (
					<div className={styles.paginationContainer}>
						<CommonPagination
							currentPage={currentPage}
							isLoading={isLoading}
							numberOfPages={totalPages}
							onPageChange={handlePageChange}
						/>
					</div>
				)}
			</div>
		</UtilityPageLayout>
	);
};

export default BulkPurchasePage;
